/**
 * Textnormalisierung fuer den Abgleich.
 *
 * Bank und MOCO schreiben dieselbe Gegenpartei praktisch nie gleich:
 * MOCO fuehrt "Müller & Sohn GmbH", auf dem Kontoauszug steht
 * "MUELLER U SOHN GMBH" oder "Mueller+Sohn". Ohne Normalisierung findet
 * ein Namensvergleich nichts.
 */
/** Rechtsformen und Zusaetze, die fuer die Identitaet einer Firma nichts beitragen. */
const LEGAL_SUFFIXES = [
    "gmbh und co kg", "gmbh co kg", "gmbh", "mbh", "ug haftungsbeschraenkt", "ug",
    "ag", "kgaa", "kg", "ohg", "gbr", "partg", "partgmbb", "ev", "eg", "ek",
    "se", "ltd", "limited", "plc", "inc", "incorporated", "llc", "corp",
    "bv", "nv", "sa", "sas", "sarl", "srl", "spa", "oy", "ab", "as", "aps",
    "und co", "co",
];
/** Praefixe, die Banken vor den eigentlichen Namen setzen. */
const BANK_NOISE = [
    "auftraggeber", "empfaenger", "zahlungspflichtiger", "beguenstigter",
    "kartenzahlung", "lastschrift", "dauerauftrag", "ueberweisung", "sepa",
    "basislastschrift", "gutschrift", "abbuchung", "einzugsermaechtigung",
];
/** Faltet deutsche Umlaute und diakritische Zeichen auf ASCII. */
export function foldDiacritics(input) {
    return input
        .replace(/ä/gi, (m) => (m === "ä" ? "ae" : "Ae"))
        .replace(/ö/gi, (m) => (m === "ö" ? "oe" : "Oe"))
        .replace(/ü/gi, (m) => (m === "ü" ? "ue" : "Ue"))
        .replace(/ß/g, "ss")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");
}
/**
 * Bringt einen Firmen- oder Personennamen auf eine vergleichbare Form:
 * klein, ohne Diakritika, ohne Rechtsform, ohne Bank-Praefixe, ohne Satzzeichen.
 *
 * "Müller & Sohn GmbH & Co. KG" und "MUELLER U SOHN GMBH CO KG"
 * werden beide zu "mueller sohn".
 */
export function normalizeName(input) {
    if (!input)
        return "";
    let s = foldDiacritics(input).toLowerCase();
    // "&" und " u. " bedeuten dasselbe wie "und" — alle drei entfernen wir gleich.
    s = s.replace(/&/g, " und ");
    s = s.replace(/\bu\.\s/g, " und ");
    s = s.replace(/[^a-z0-9]+/g, " ").trim();
    s = s.replace(/\bund\b/g, " ");
    for (const noise of BANK_NOISE) {
        s = s.replace(new RegExp(`\\b${noise}\\b`, "g"), " ");
    }
    // Laengste Suffixe zuerst, sonst frisst "co" das "co kg" an.
    for (const suffix of [...LEGAL_SUFFIXES].sort((a, b) => b.length - a.length)) {
        s = s.replace(new RegExp(`\\b${suffix}\\b`, "g"), " ");
    }
    return s.replace(/\s+/g, " ").trim();
}
/** IBAN ohne Leerzeichen, in Grossbuchstaben. */
export function normalizeIban(iban) {
    if (!iban)
        return null;
    const s = iban.replace(/[\s-]/g, "").toUpperCase();
    return /^[A-Z]{2}\d{2}[A-Z0-9]{6,30}$/.test(s) ? s : null;
}
/**
 * Reduziert einen Text auf Grossbuchstaben und Ziffern.
 *
 * Damit findet "Rg. R2026-0042 vom 3.4." dieselbe Rechnung wie
 * "R 2026 0042" oder "R20260042" — Kunden tippen Rechnungsnummern
 * in jeder erdenklichen Schreibweise.
 */
export function alphanumeric(input) {
    if (!input)
        return "";
    return foldDiacritics(input).toUpperCase().replace(/[^A-Z0-9]/g, "");
}
/**
 * Kuerzeste Referenz, die noch als eindeutig gilt.
 *
 * Unterhalb davon suchen wir gar nicht erst: eine dreistellige Rechnungsnummer
 * findet sich zufaellig in jeder zweiten IBAN oder Mandatsreferenz und wuerde
 * lauter Falschtreffer erzeugen.
 */
export const MIN_REFERENCE_LENGTH = 5;
/**
 * Prueft, ob eine Referenz (Rechnungsnummer) in einem Freitext vorkommt.
 *
 * Zusaetzlich zur vollstaendigen Referenz wird ihr Ziffernkern geprueft
 * ("R2026-0042" → "20260042"), weil manche Kunden den Buchstabenpraefix
 * beim Ueberweisen weglassen.
 */
export function referenceOccursIn(reference, haystack) {
    const needle = alphanumeric(reference);
    const hay = alphanumeric(haystack);
    if (needle.length < MIN_REFERENCE_LENGTH || hay.length === 0)
        return false;
    if (hay.includes(needle))
        return true;
    const digitsOnly = needle.replace(/[^0-9]/g, "");
    if (digitsOnly.length >= MIN_REFERENCE_LENGTH && hay.includes(digitsOnly))
        return true;
    return false;
}
// ---- Aehnlichkeit ------------------------------------------------------------
function trigrams(s) {
    const padded = `  ${s} `;
    const out = new Set();
    for (let i = 0; i < padded.length - 2; i++)
        out.add(padded.slice(i, i + 3));
    return out;
}
/**
 * Aehnlichkeit zweier Namen als Wert zwischen 0 und 1 (Dice-Koeffizient ueber Trigramme).
 *
 * Trigramme statt Levenshtein, weil sie gegen Wortumstellungen und
 * abgeschnittene Namen robuster sind — und der Kontoauszug schneidet
 * Namen regelmaessig nach 27 Zeichen ab.
 */
export function nameSimilarity(a, b) {
    const na = normalizeName(a);
    const nb = normalizeName(b);
    if (!na || !nb)
        return 0;
    if (na === nb)
        return 1;
    // Ein abgeschnittener Name ist ein Praefix des vollen — das ist ein starkes
    // Signal und soll nicht durch die fehlende Endung abgewertet werden.
    if (na.length >= 6 && nb.startsWith(na))
        return 0.95;
    if (nb.length >= 6 && na.startsWith(nb))
        return 0.95;
    const ta = trigrams(na);
    const tb = trigrams(nb);
    let shared = 0;
    for (const t of ta)
        if (tb.has(t))
            shared++;
    return (2 * shared) / (ta.size + tb.size);
}
//# sourceMappingURL=normalize.js.map